import {
    getPricingForScope,
    getStorePricingOverrides,
    IPricing,
    IStorePricingOverride,
    PricingGroupSlug,
    removeStorePricingOverride,
    upsertStorePricingOverride,
} from "@/api/settings.ts";
import ReplayIcon from "@mui/icons-material/Replay";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { usePricingScope } from "../pricingScope";

/**
 * Describes how one pricing group is read out of the merged sheet.
 *
 * All six groups are key/value pairs once flattened, so one renderer covers them. The
 * alternative — teaching each of the six existing global forms to also write override
 * payloads — is more code and puts the global write path at risk for a store feature.
 */
interface GroupSpec {
    slug: PricingGroupSlug;
    label: string;
    /** Reads the group out of the merged pricing sheet as {key, value} rows. */
    rows: (pricing: IPricing) => { key: string; value: number }[];
}

// The API speaks CENTS. Every other pricing form on this screen speaks DOLLARS -- the
// global "Distance Cost" field shows 1.5 for $1.50/mile. This editor must match, or an
// admin typing a negotiated 2.25 sets the rate to two cents per mile while the field
// beside it means something else entirely.
const centsToDollars = (cents: number) => (cents / 100).toFixed(2);
const dollarsToCents = (dollars: number) => Math.round(dollars * 100);

/**
 * Pull a displayable message out of an axios error.
 *
 * FastAPI answers a handler-raised HTTPException with a string `detail`, but a request
 * that fails schema validation answers 422 with an ARRAY of error objects. Passing that
 * array straight to the snackbar throws "Objects are not valid as a React child", so a
 * validation failure would crash the screen instead of reporting itself.
 */
const errorMessage = (error: unknown, fallback: string): string => {
    const detail = (
        error as { response?: { data?: { detail?: unknown } } } | undefined
    )?.response?.data?.detail;

    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const first = detail[0] as { msg?: string } | undefined;
        return first?.msg ?? fallback;
    }
    return fallback;
};

const GROUPS: GroupSpec[] = [
    {
        slug: "item-size",
        label: "Item Size Base Prices",
        rows: (p) =>
            p.item_sizes.map((r) => ({
                key: r.size_name,
                value: r.base_price,
            })),
    },
    {
        slug: "floor-level",
        label: "Floor Levels",
        rows: (p) =>
            p.floor_levels.map((r) => ({ key: r.level, value: r.cost })),
    },
    {
        slug: "delivery-window",
        label: "Delivery Windows",
        rows: (p) =>
            p.delivery_windows.map((r) => ({
                key: r.window_type,
                value: r.additional_cost,
            })),
    },
    {
        slug: "misc-cost",
        label: "Misc Costs (incl. per-mile rate)",
        rows: (p) =>
            p.misc_costs.map((r) => ({
                key: r.cost_name,
                value: r.cost_value,
            })),
    },
];

interface RowProps {
    rowKey: string;
    value: number;
    override?: IStorePricingOverride;
    onSave: (key: string, value: number) => void;
    onRevert: (key: string) => void;
    busy: boolean;
}

const OverrideRow = ({
    rowKey,
    value,
    override,
    onSave,
    onRevert,
    busy,
}: RowProps) => {
    // Seeded once per mount. The parent remounts this row whenever the server-side
    // value or override identity changes (see the key it passes), which is what keeps
    // the draft from going stale after a save or a revert. Without that remount, a
    // revert would leave the old override showing in the field while the store actually
    // paid the global price -- and the next blur would silently re-save it.
    // Held and edited in DOLLARS; converted to cents only when saving.
    const [draft, setDraft] = useState<string>(centsToDollars(value));
    const isOverridden = override !== undefined && !override.suppressed;
    const isSuppressed = override?.suppressed === true;
    const dirty = draft !== centsToDollars(value);
    const parsedDollars = Number(draft);
    const invalid =
        draft === "" || Number.isNaN(parsedDollars) || parsedDollars < 0;

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography
                sx={{
                    minWidth: 160,
                    textDecoration: isSuppressed ? "line-through" : undefined,
                }}
            >
                {rowKey}
            </Typography>

            <TextField
                size="small"
                type="number"
                // Wide enough for a five-figure amount plus cents. At the default width
                // "20.00" clipped to "20." on screen, which on a pricing field reads as
                // a different number.
                sx={{ width: 130 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                    ),
                }}
                value={draft}
                disabled={busy || isSuppressed}
                onChange={(event) => setDraft(event.target.value)}
                inputProps={{ step: "0.01", min: "0" }}
                onBlur={() => {
                    // Negative and unparseable values are rejected here rather than
                    // sent: the API answers a negative amount with a 422 whose detail
                    // is a pydantic error ARRAY, not a string, which the snackbar
                    // cannot render.
                    if (dirty && !invalid)
                        onSave(rowKey, dollarsToCents(parsedDollars));
                }}
                error={dirty && invalid}
                helperText={
                    dirty && invalid
                        ? "Enter an amount of 0 or more"
                        : undefined
                }
            />

            {/* Three distinct states, never two. A suppressed tier stays visible and
                struck through rather than disappearing: hidden, it would be
                indistinguishable from a tier that never existed, and there would be
                nothing to click to restore it. */}
            {isSuppressed ? (
                <Chip size="small" color="warning" label="Suppressed" />
            ) : isOverridden ? (
                <Chip size="small" color="primary" label="Overridden" />
            ) : (
                <Chip size="small" variant="outlined" label="Inherited" />
            )}

            {dirty && <Chip size="small" color="info" label="Unsaved" />}

            {(isOverridden || isSuppressed) && (
                /* Revert is a DISTINCT action from typing the global value back in.
                   Typing leaves a real row that stops tracking global forever, so the
                   two must not look the same — the badge above persists after typing a
                   matching number, and only this control restores inheritance. */
                <Tooltip title="Revert to the global value">
                    <span>
                        <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => onRevert(rowKey)}
                        >
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
};

/**
 * Editor for a single store's pricing overrides.
 *
 * Needs TWO reads and they answer different questions: the merged sheet supplies the
 * VALUES a store currently pays, and the store's own rows supply the STATE — which of
 * those values an admin set. A merged value looks identical whether it came from global
 * or from an override, so state cannot be derived from it.
 */
export const StorePricingEditor = () => {
    const { storeId, storeName } = usePricingScope();
    const queryClient = useQueryClient();

    // Both keys include the store id. Without it, selecting a second store would serve
    // the first store's cached pricing under the second store's name.
    const pricingQuery = useQuery({
        queryKey: ["effective-pricing", storeId],
        queryFn: () => getPricingForScope(storeId),
        enabled: storeId !== null,
    });

    const overridesQuery = useQuery({
        queryKey: ["store-pricing-overrides", storeId],
        queryFn: () => getStorePricingOverrides(storeId as number),
        enabled: storeId !== null,
    });

    const refresh = () => {
        queryClient.invalidateQueries({
            queryKey: ["effective-pricing", storeId],
        });
        queryClient.invalidateQueries({
            queryKey: ["store-pricing-overrides", storeId],
        });
    };

    const saveMutation = useMutation({
        mutationFn: ({
            group,
            key,
            value,
        }: {
            group: PricingGroupSlug;
            key: string;
            value: number;
        }) => upsertStorePricingOverride(storeId as number, group, key, value),
        onSuccess: () => {
            enqueueSnackbar("Override saved", { variant: "success" });
            refresh();
        },
        onError: (error: unknown) =>
            enqueueSnackbar(errorMessage(error, "Could not save override"), {
                variant: "error",
            }),
    });

    const revertMutation = useMutation({
        mutationFn: ({
            group,
            key,
        }: {
            group: PricingGroupSlug;
            key: string;
        }) => removeStorePricingOverride(storeId as number, group, key),
        onSuccess: () => {
            enqueueSnackbar("Reverted to global", { variant: "success" });
            refresh();
        },
        onError: (error: unknown) =>
            enqueueSnackbar(errorMessage(error, "Could not revert"), {
                variant: "error",
            }),
    });

    if (storeId === null) return null;
    if (pricingQuery.isPending || overridesQuery.isPending) {
        return <Skeleton height={400} />;
    }
    if (pricingQuery.isError || overridesQuery.isError || !pricingQuery.data) {
        return (
            <Alert severity="error">
                Could not load pricing for this store.
            </Alert>
        );
    }

    // Guards against a slow response from a previously selected store painting over the
    // current one. The API echoes the scope it resolved for precisely so this is cheap.
    if (overridesQuery.data && overridesQuery.data.store_id !== storeId) {
        return <Skeleton height={400} />;
    }

    const overridesByGroup = overridesQuery.data?.groups ?? {};
    // Groups this screen does not render yet. Counted so the notice below can be
    // specific rather than reassuring.
    const ladderOverrideCount = ["high-value", "distance-surcharge"].reduce(
        (total, slug) => total + (overridesByGroup[slug]?.length ?? 0),
        0
    );
    const busy = saveMutation.isPending || revertMutation.isPending;

    return (
        <Stack spacing={3}>
            <Alert severity="info">
                Editing <strong>{storeName}</strong>. Values marked Inherited
                follow the global sheet and will move when global pricing
                changes. Overridden values stay put until reverted.
            </Alert>

            {GROUPS.map((group) => {
                const overrides = overridesByGroup[group.slug] ?? [];
                const byKey = new Map(overrides.map((o) => [o.key, o]));

                return (
                    <Card key={group.slug}>
                        <CardContent>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                {group.label}
                            </Typography>
                            <Stack spacing={2}>
                                {group.rows(pricingQuery.data).map((row) => (
                                    <OverrideRow
                                        /* The resolved value and override identity are
                                           part of the key ON PURPOSE. The row holds its
                                           edit field in local state seeded at mount, so
                                           without a remount a revert would leave the old
                                           override showing while the store actually paid
                                           the global price -- and the next blur would
                                           silently re-save it. Changing the key discards
                                           the stale draft exactly when the server value
                                           changes, and only then. */
                                        key={[
                                            group.slug,
                                            row.key,
                                            row.value,
                                            byKey.get(row.key)?.id ?? "none",
                                            byKey.get(row.key)?.suppressed ??
                                                false,
                                        ].join(":")}
                                        rowKey={row.key}
                                        value={row.value}
                                        override={byKey.get(row.key)}
                                        busy={busy}
                                        onSave={(key, value) =>
                                            saveMutation.mutate({
                                                group: group.slug,
                                                key,
                                                value,
                                            })
                                        }
                                        onRevert={(key) =>
                                            revertMutation.mutate({
                                                group: group.slug,
                                                key,
                                            })
                                        }
                                    />
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}

            <Box>
                {/* This screen renders the four keyed groups. Ladder overrides ARE
                    supported by the API, so a store can already have them — and
                    silently not showing them would leave an admin unable to see, let
                    alone revert, a tier that is changing what the store is charged.
                    Say so, with the count, rather than implying none exist. */}
                <Alert severity={ladderOverrideCount > 0 ? "warning" : "info"}>
                    Threshold ladders (high-value upcharges and distance
                    surcharges) are edited globally on this screen for now.
                    {ladderOverrideCount > 0 && (
                        <>
                            {" "}
                            <strong>
                                {storeName} has {ladderOverrideCount}{" "}
                                store-specific ladder{" "}
                                {ladderOverrideCount === 1 ? "row" : "rows"}
                            </strong>{" "}
                            that are affecting its prices but are not shown
                            here. They can be viewed and changed through the
                            store pricing API until this screen covers them.
                        </>
                    )}
                </Alert>
            </Box>
        </Stack>
    );
};
